import { FormEvent, useEffect, useState, type CSSProperties } from "react";
import { CalendarDays, ShieldCheck, Truck, Wrench } from "lucide-react";
import { FunctionalApp as Dashboard } from "./FunctionalApp";
import { PlatformAdmin } from "./PlatformAdmin";
import { PublicBooking } from "./PublicBooking";

type AuthState = "checking" | "signed-out" | "signed-in" | "mfa" | "mfa-setup" | "mfa-recovery" | "change-password";
export type SessionPayload = {
  user: {
    id: string;
    name: string;
    email: string;
    isTenantOwner: boolean;
    isStaff: boolean;
    mustChangePassword?: boolean;
    permissions: string[];
    products: ("MAINTENANCE" | "DISPATCH" | "APPOINTMENTS")[];
    groups: {
      id: string;
      name: string;
      product: "MAINTENANCE" | "DISPATCH" | "APPOINTMENTS" | null;
    }[];
  };
  tenant: { id: string; name: string; slug: string; billingState: string };
};
type Branding = {
  companyName: string;
  logoUrl: string | null;
  primaryColor: string;
  accentColor: string;
  signInHeading: string;
  welcomeMessage: string;
};
const defaultBranding: Branding = {
  companyName: "TaskRoute",
  logoUrl: null,
  primaryColor: "#102a43",
  accentColor: "#12a594",
  signInHeading: "Sign in to your workspace",
  welcomeMessage: "Maintenance and dispatch operations, securely in one place.",
};
const apiOrigin = location.hostname.endsWith(".taskroute.co.uk")
  ? "https://taskroute.co.uk"
  : "";
const api = (path: string) => `${apiOrigin}${path}`;
const tenantHost =
  location.hostname.endsWith(".taskroute.co.uk") &&
  location.hostname !== "www.taskroute.co.uk";
const hostnameTenant = tenantHost
  ? location.hostname.slice(0, -".taskroute.co.uk".length)
  : "";

export function AuthenticatedApp() {
  const bookingPage = location.pathname === "/book";
  const kioskPage = location.pathname === "/kiosk";
  const publicHome =
    location.hostname === "taskroute.co.uk" && location.pathname === "/";
  const tenantRoot = tenantHost && location.pathname === "/";
  const [showStaffLogin, setShowStaffLogin] = useState(false);
  const [tenantHasAppointments, setTenantHasAppointments] = useState<boolean | null>(null);
  const controlPage =
    location.hostname === "taskroute.co.uk" && location.pathname === "/control";
  const [auth, setAuth] = useState<AuthState>("checking");
  const [company, setCompany] = useState(() =>
    controlPage
      ? "taskroute-admin"
      : hostnameTenant ||
        localStorage.getItem("taskroute-tenant") ||
        "northstar",
  );
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [mfaCode, setMfaCode] = useState("");
  const [mfaSetup, setMfaSetup] = useState<{secret:string;qrDataUrl:string}|null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [session, setSession] = useState<SessionPayload | null>(null);
  const [branding, setBranding] = useState<Branding>(defaultBranding);

  async function loadSession(slug: string, signal?: AbortSignal) {
    const response = await fetch(api("/api/me"), {
      credentials: "include",
      headers: { "x-tenant-slug": slug },
      signal,
    });
    if (!response.ok) {
      setSession(null);
      setAuth((current) =>
        current === "mfa" || current === "mfa-setup" || current === "mfa-recovery"
          ? current
          : "signed-out",
      );
      return false;
    }
    const next: SessionPayload = await response.json();
    localStorage.setItem("taskroute-tenant", next.tenant.slug);
    if (next.user.isStaff && next.tenant.slug === "taskroute-admin") {
      if (location.pathname !== "/control" || location.hostname !== "taskroute.co.uk") {
        location.replace("https://taskroute.co.uk/control");
        return true;
      }
      setSession(next);
      setAuth("signed-in");
      return true;
    }
    if (
      !tenantHost &&
      !publicHome &&
      location.hostname === "taskroute.co.uk" &&
      location.pathname !== "/control"
    ) {
      location.replace(`https://${next.tenant.slug}.taskroute.co.uk/`);
      return true;
    }
    setSession(next);
    if (next.user.mustChangePassword) {
      setAuth("change-password");
      return true;
    }
    setAuth("signed-in");
    return true;
  }
  useEffect(() => {
    const controller = new AbortController();
    loadSession(company, controller.signal).catch((error: { name?: string }) => {
      if (error?.name !== "AbortError") setAuth("signed-out");
    });
    // Check if tenant has appointments enabled (for home page routing)
    if (tenantRoot && hostnameTenant) {
      fetch(api("/api/public/appointments/catalog"), {
        headers: { "x-tenant-slug": hostnameTenant },
        signal: controller.signal,
      }).then(r => setTenantHasAppointments(r.ok))
        .catch(() => setTenantHasAppointments(false));
    }
    return () => controller.abort();
  }, []);
  useEffect(() => {
    if (auth === "signed-in") return;
    const controller = new AbortController(),
      slug = company.trim().toLowerCase();
    const timer = setTimeout(() => {
      if (!slug) return setBranding(defaultBranding);
      fetch(api("/api/branding"), {
        headers: { "x-tenant-slug": slug },
        signal: controller.signal,
      })
        .then(async (response) => {
          if (response.ok) setBranding(await response.json());
          else setBranding(defaultBranding);
        })
        .catch(() => setBranding(defaultBranding));
    }, 300);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [company, auth]);

  async function login(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const slug = (hostnameTenant || company).trim().toLowerCase();
      const response = await fetch(api("/api/auth/login"), {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json", "x-tenant-slug": slug },
        body: JSON.stringify({ email, password }),
      });
      if (!response.ok) {
        setError(
          response.status === 401
            ? "The email or password is incorrect."
            : "Unable to sign in. Check the company address and try again.",
        );
        return;
      }
      const result = await response.json();
      if (result.mfaRequired) { setAuth("mfa"); return; }
      if (result.mfaSetupRequired) {
        const setup = await fetch(api("/api/auth/mfa/setup"), { method: "POST", credentials: "include", headers: { "x-tenant-slug": slug } });
        if (!setup.ok) throw new Error("MFA setup unavailable");
        setMfaSetup(await setup.json()); setAuth("mfa-setup"); return;
      }
      localStorage.setItem("taskroute-tenant", slug);
      if (
        !tenantHost &&
        location.hostname === "taskroute.co.uk" &&
        location.pathname !== "/control"
      ) {
        location.assign(`https://${slug}.taskroute.co.uk/`);
        return;
      }
      await loadSession(slug);
    } catch {
      setError("TaskRoute could not reach the server. Try again shortly.");
    } finally {
      setSubmitting(false);
    }
  }
  async function submitMfa(event: FormEvent) {
    event.preventDefault(); setSubmitting(true); setError("");
    try {
      const slug = (hostnameTenant || company).trim().toLowerCase(), endpoint = auth === "mfa-setup" ? "/api/auth/mfa/setup/confirm" : "/api/auth/mfa/verify";
      const response = await fetch(api(endpoint), { method: "POST", credentials: "include", headers: { "content-type": "application/json", "x-tenant-slug": slug }, body: JSON.stringify({ code: mfaCode }) });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) { setError(String(body.error || "Invalid authentication code").replaceAll("_", " ")); return; }
      if (Array.isArray(body.recoveryCodes)) { setRecoveryCodes(body.recoveryCodes); setAuth("mfa-recovery"); return; }
      localStorage.setItem("taskroute-tenant", slug); await loadSession(slug);
    } catch { setError("TaskRoute could not verify the authentication code."); }
    finally { setSubmitting(false); }
  }

  if (bookingPage) return <PublicBooking />;
  if (publicHome) return <PublicHome />;
  // Tenant root — show booking page if tenant has appointments and user not signed in
  if (tenantRoot && auth !== "signed-in" && !showStaffLogin && tenantHasAppointments === true)
    return (
      <div style={{position:"relative"}}>
        <PublicBooking />
        <button
          onClick={() => setShowStaffLogin(true)}
          style={{position:"fixed",bottom:20,right:20,background:"rgba(16,42,67,.85)",color:"#fff",border:0,borderRadius:8,padding:"10px 18px",fontSize:13,fontWeight:700,cursor:"pointer",zIndex:100,backdropFilter:"blur(4px)"}}>
          Staff login
        </button>
      </div>
    );
  if (auth === "checking")
    return (
      <div className="auth-shell">
        <div className="auth-loading">Loading TaskRoute…</div>
      </div>
    );
  if (auth === "change-password" && session)
    return <ChangePasswordScreen session={session} onDone={() => setAuth("signed-in")} />;
  if (auth === "signed-in" && session) {
    if (controlPage)
      return session.user.isStaff &&
        session.tenant.slug === "taskroute-admin" ? (
        <PlatformAdmin session={session} />
      ) : (
        <div className="auth-shell">
          <div className="auth-loading">
            TaskRoute Administration access required.
          </div>
        </div>
      );
    if (["TERMINATED", "DELETED"].includes(session.tenant.billingState))
      return <ExportOnlyAccount session={session} />;
    return <Dashboard session={session} />;
  }
  const brandStyle = {
    "--tenant-primary": branding.primaryColor,
    "--tenant-accent": branding.accentColor,
  } as CSSProperties;
  return (
    <div className="auth-shell tenant-branded" style={brandStyle}>
      <section className="auth-panel">
        <div className="auth-brand">
          {branding.logoUrl ? (
            <img src={branding.logoUrl} alt={`${branding.companyName} logo`} />
          ) : (
            <span>◆</span>
          )}
          <span>
            {controlPage ? "TaskRoute Control" : branding.companyName}
          </span>
          <small>Powered by TaskRoute</small>
        </div>
        <div className="auth-copy">
          <ShieldCheck />
          <div>
            <h1>
              {controlPage
                ? "Platform administrator sign in"
                : branding.signInHeading}
            </h1>
            <p>
              {controlPage
                ? "Secure access for authorised TaskRoute staff only."
                : branding.welcomeMessage}
            </p>
          </div>
        </div>
        {auth === "mfa-recovery" ? <div className="mfa-recovery"><h2>Save your recovery codes</h2><p>Each code can be used once if you lose access to your authenticator. Store them somewhere secure; TaskRoute cannot display them again.</p><pre>{recoveryCodes.join("\n")}</pre><button type="button" onClick={() => { const blob=new Blob([`TaskRoute recovery codes\n\n${recoveryCodes.join("\n")}\n`],{type:"text/plain"}),href=URL.createObjectURL(blob),link=document.createElement("a");link.href=href;link.download="TaskRoute-recovery-codes.txt";link.click();URL.revokeObjectURL(href); }}>Download codes</button><button type="button" onClick={() => loadSession((hostnameTenant || company).trim().toLowerCase())}>I have saved these codes</button></div> : auth === "mfa" || auth === "mfa-setup" ? <form onSubmit={submitMfa}>
          {auth === "mfa-setup" ? <div className="mfa-setup"><p>TaskRoute Control requires multi-factor authentication. Scan this code with Microsoft Authenticator, Google Authenticator, 1Password or another TOTP app.</p>{mfaSetup?.qrDataUrl ? <img src={mfaSetup.qrDataUrl} alt="Authenticator setup QR code" /> : null}<details><summary>Cannot scan the code?</summary><code>{mfaSetup?.secret}</code></details></div> : <p>Enter the six-digit code from your authenticator app.</p>}
          <label>Authentication code{auth === "mfa" ? " or recovery code" : ""}<input inputMode={auth === "mfa-setup" ? "numeric" : "text"} autoComplete="one-time-code" pattern={auth === "mfa-setup" ? "[0-9]{6}" : undefined} maxLength={auth === "mfa-setup" ? 6 : 20} value={mfaCode} onChange={e => setMfaCode(auth === "mfa-setup" ? e.target.value.replace(/\D/g, "") : e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ""))} required autoFocus /></label>
          {error ? <p className="auth-error" role="alert">{error}</p> : null}
          <button type="submit" disabled={submitting || (auth === "mfa-setup" ? mfaCode.length !== 6 : mfaCode.length < 6)}>{submitting ? "Verifying…" : auth === "mfa-setup" ? "Enable MFA and continue" : "Verify and sign in"}</button>
        </form> : <form onSubmit={login}>
          {!tenantHost && !controlPage ? (
            <label>
              Company address
              <div className="slug-field">
                <input
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  autoComplete="organization"
                  required
                />
                <span>.taskroute.co.uk</span>
              </div>
            </label>
          ) : null}
          <label>
            Email address
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
              required
              autoFocus
            />
          </label>
          <label>
            Password
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
          </label>
          {error ? (
            <p className="auth-error" role="alert">
              {error}
            </p>
          ) : null}
          <button type="submit" disabled={submitting}>
            {submitting ? "Signing in…" : "Sign in"}
          </button>
        </form>}
        <footer>
          <Wrench />{" "}
          {controlPage
            ? "Restricted platform administration area."
            : `Need help? Contact your ${branding.companyName} administrator.`}
          {showStaffLogin && tenantHasAppointments && (
            <button onClick={() => setShowStaffLogin(false)}
              style={{display:"block",marginTop:12,background:"none",border:"1px solid #c8d5db",borderRadius:6,padding:"8px 14px",fontSize:13,cursor:"pointer",color:"#506573",width:"100%"}}>
              ← Back to booking page
            </button>
          )}
        </footer>
      </section>
      <aside className="auth-art">
        <div>
          <strong>
            {controlPage
              ? "Manage TaskRoute securely."
              : "Keep operations moving."}
          </strong>
          <p>
            {controlPage
              ? "Create and govern customer and sandbox workspaces from one isolated administration account."
              : "Plan maintenance, respond to breakdowns and coordinate dispatch from a single operational record."}
          </p>
        </div>
      </aside>
    </div>
  );
}

function ChangePasswordScreen({ session, onDone }: { session: SessionPayload; onDone: () => void }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (password !== confirm) { setError("Passwords do not match."); return; }
    if (password.length < 12) { setError("Password must be at least 12 characters."); return; }
    setBusy(true); setError("");
    try {
      const r = await fetch(api("/api/auth/change-password"), {
        method: "POST", credentials: "include",
        headers: { "content-type": "application/json", "x-tenant-slug": session.tenant.slug },
        body: JSON.stringify({ password }),
      });
      if (!r.ok) { const b = await r.json().catch(() => ({})); setError(String(b.error || "Unable to update password").replaceAll("_", " ")); return; }
      onDone();
    } catch { setError("Unable to reach the server. Try again."); }
    finally { setBusy(false); }
  }
  return (
    <div className="auth-shell">
      <section className="auth-panel">
        <div className="auth-brand"><span>◆</span><span>TaskRoute</span><small>Powered by TaskRoute</small></div>
        <div className="auth-copy"><ShieldCheck /><div><h1>Set your password</h1><p>Your account has a temporary password. Please set a new one before continuing.</p></div></div>
        <form onSubmit={submit}>
          <label>New password (minimum 12 characters)<input autoFocus type="password" autoComplete="new-password" required minLength={12} value={password} onChange={e => setPassword(e.target.value)} /></label>
          <label>Confirm new password<input type="password" autoComplete="new-password" required minLength={12} value={confirm} onChange={e => setConfirm(e.target.value)} /></label>
          {error ? <p className="auth-error" role="alert">{error}</p> : null}
          <button type="submit" disabled={busy || password.length < 12}>{busy ? "Saving…" : "Set password and continue"}</button>
        </form>
        <footer><Wrench /> Contact your administrator if you need help.</footer>
      </section>
    </div>
  );
}

function ExportOnlyAccount({ session }: { session: SessionPayload }) {
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const deleted = session.tenant.billingState === "DELETED";
  async function download() {
    setBusy(true); setError("");
    try {
      const response = await fetch(api("/api/exports/complete"), { credentials: "include", headers: { "x-tenant-slug": session.tenant.slug } });
      if (!response.ok) throw new Error("The export could not be prepared. Please contact TaskRoute support.");
      const href = URL.createObjectURL(await response.blob());
      const link = document.createElement("a"); link.href = href; link.download = `taskroute-${session.tenant.slug}-export.json`; link.click();
      setTimeout(() => URL.revokeObjectURL(href), 60000);
    } catch (failure: any) { setError(failure.message); }
    finally { setBusy(false); }
  }
  async function signOut() { await fetch(api("/api/auth/logout"), { method: "POST", credentials: "include", headers: { "x-tenant-slug": session.tenant.slug } }); location.reload(); }
  return <div className="account-access-shell"><section><span className="account-access-brand">◆ TaskRoute</span><h1>{deleted ? "Account data deleted" : "Export-only access"}</h1><p>{deleted ? "This account's operational data has been deleted after the retention period. Export is no longer available." : `${session.tenant.name} is no longer active. You can download the remaining data as a portable JSON file using a standard browser; operational features are unavailable.`}</p>{error ? <p role="alert" className="auth-error">{error}</p> : null}<div>{!deleted ? <button disabled={busy} onClick={download}>{busy ? "Preparing export…" : "Download complete export"}</button> : null}<button className="secondary" onClick={signOut}>Sign out</button></div></section></div>;
}

function PublicHome() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);

  async function joinWaitlist(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await fetch("/api/waitlist", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email }),
      });
      setSubmitted(true);
    } catch {}
    finally { setBusy(false); }
  }

  return (
    <div className="public-home">
      <header>
        <a className="public-logo" href="/"><span>◆</span>TaskRoute</a>
        <nav>
          <a href="#maintenance">Maintenance</a>
          <a href="#dispatch">Dispatch</a>
          <a href="#appointments">Appointments</a>
          <a href="#security">Security</a>
        </nav>
        <a className="public-login" href="/login">Sign in</a>
      </header>
      <main>
        <section className="public-hero">
          <div>
            <span className="public-coming-soon">🚧 Coming Soon</span>
            <span className="public-kicker">Operations and bookings, properly connected</span>
            <h1>Run maintenance, dispatch and appointments with clarity.</h1>
            <p>
              TaskRoute provides three separately enabled products in one secure
              platform: Maintenance, Dispatch and Appointments. We're putting
              the finishing touches on before opening to new customers.
            </p>
            {submitted ? (
              <div className="public-waitlist-thanks">
                ✓ You're on the list — we'll be in touch soon.
              </div>
            ) : (
              <form className="public-waitlist" onSubmit={joinWaitlist}>
                <input
                  type="email"
                  required
                  placeholder="Your email address"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                />
                <button type="submit" disabled={busy}>
                  {busy ? "Joining…" : "Join the waiting list"}
                </button>
              </form>
            )}
            <p className="public-waitlist-note">No spam. Just a message when we're ready.</p>
          </div>
          <div className="public-preview">
            <div>
              <small>LAUNCHING SOON</small>
              <strong>One platform.<br />Three separate products.</strong>
            </div>
            <ul>
              <li><Wrench />Maintenance and CMMS</li>
              <li><Truck />Dispatch and pallet tracking</li>
              <li><CalendarDays />Appointments and service calendars</li>
              <li><ShieldCheck />Tenant-isolated and permission controlled</li>
            </ul>
          </div>
        </section>
        <section className="public-products" id="products">
          <article id="maintenance">
            <Wrench />
            <small>MAINTENANCE</small>
            <h2>Prevent downtime. Respond with clarity.</h2>
            <p>Manage assets, breakdowns, work orders, planned maintenance, spare parts, temporary repairs, sign-off and downtime reporting.</p>
            <ul>
              <li>Work claiming, assignments and assistants</li>
              <li>PPM schedules and reactivation catch-up</li>
              <li>Inventory, bin locations and stock history</li>
              <li>Production stopped escalation</li>
            </ul>
            <span className="product-badge available">Available in alpha</span>
          </article>
          <article id="dispatch">
            <Truck />
            <small>DISPATCH</small>
            <h2>Know where every order and pallet is.</h2>
            <p>Coordinate picking, runners, trailers, export loads, customer collections, manifests and signatures without losing the operational trail.</p>
            <ul>
              <li>Individual pallet tracking</li>
              <li>Two-scan trailer loading</li>
              <li>Runner queues and wrong-load protection</li>
              <li>Signed manifests and status search</li>
            </ul>
            <span className="product-badge coming-soon">Coming soon</span>
          </article>
          <article id="appointments">
            <CalendarDays />
            <small>APPOINTMENTS</small>
            <h2>Turn availability into confirmed bookings.</h2>
            <p>Configure staff, services, prices, duration, cleanup time, online-booking discounts and customer appointments without double-booking.</p>
            <ul>
              <li>Staff-specific services, prices and durations</li>
              <li>Availability and conflict-safe calendars</li>
              <li>Telephone, walk-in and online booking sources</li>
              <li>Stripe and Square connected-account setup</li>
            </ul>
            <span className="product-badge coming-soon">Coming soon</span>
          </article>
        </section>
        <section className="public-security" id="security">
          <div>
            <small>BUILT FOR ACCOUNTABILITY</small>
            <h2>Customer data stays separated.</h2>
          </div>
          <p>
            Each company has its own workspace, product seats, groups and
            permissions. Support access is temporary, customer controlled and
            audited. Suspended customers retain export access without receiving
            free operational workflows.
          </p>
        </section>
        <section className="public-cta">
          <h2>Interested in TaskRoute?</h2>
          <p>We're currently in alpha with a small number of businesses. Join the waiting list and we'll reach out when we're ready for new customers.</p>
          {!submitted ? (
            <form className="public-waitlist" onSubmit={joinWaitlist}>
              <input type="email" required placeholder="Your email address" value={email} onChange={e => setEmail(e.target.value)} />
              <button type="submit" disabled={busy}>{busy ? "Joining…" : "Join the waiting list"}</button>
            </form>
          ) : (
            <div className="public-waitlist-thanks">✓ You're on the list — we'll be in touch soon.</div>
          )}
        </section>
      </main>
      <footer>
        <span>© {new Date().getFullYear()} TaskRoute — a <a href="https://callumscc.co.uk">Callum's Custom Creations</a> product</span>
        <a href="/login">Customer sign in</a>
      </footer>
    </div>
  );
}
